import { useEffect, useMemo, useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { apiClient } from '../../../api';
import type { PortfolioRow } from './types';
import AppDetailPanel from './AppDetailPanel';

const DISPS = ['any', 'BUY', 'HOLD', 'SELL'] as const;
const CATS = ['any', '0', '1', '2', '3', 'na'] as const;
const SCOPES = ['any', 'in', 'oos'] as const;
const ASSESSED = ['any', 'yes', 'no'] as const;
const PROGRESS = ['any', '1- Cloud Deployed', '2 - In Progress Complete before DCM', '3 - In Progress Post DCM Completion', '4- Evaluation Required', 'N/A'] as const;

export default function AppPortfolioGrid({ refreshKey }: { refreshKey: number }) {
  const [items, setItems] = useState<PortfolioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [disposition, setDisposition] = useState<(typeof DISPS)[number]>('any');
  const [category, setCategory] = useState<(typeof CATS)[number]>('any');
  const [scope, setScope] = useState<(typeof SCOPES)[number]>('any');
  const [assessed, setAssessed] = useState<(typeof ASSESSED)[number]>('any');
  const [progress, setProgress] = useState<(typeof PROGRESS)[number]>('any');
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: Record<string, string> = {};
    if (qDebounced.trim()) params.q = qDebounced.trim();
    if (disposition !== 'any') params.disposition = disposition;
    if (category !== 'any') params.category = category;
    if (scope !== 'any') params.scope = scope;
    if (assessed !== 'any') params.assessed = assessed;
    if (progress !== 'any') params.progress = progress;
    apiClient.get('/api/cloud-portfolio/apps', { params })
      .then((r) => { if (!cancelled) setItems(r.data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey, qDebounced, disposition, category, scope, assessed, progress]);

  const activeFilterCount =
    (disposition !== 'any' ? 1 : 0) + (category !== 'any' ? 1 : 0) +
    (scope !== 'any' ? 1 : 0) + (assessed !== 'any' ? 1 : 0) +
    (progress !== 'any' ? 1 : 0);

  const selectedRow = useMemo(() => items.find((r) => r.eai_nbr === selected) || null, [items, selected]);

  return (
    <div className="flex gap-4 min-h-[420px]">
      <div className={`min-w-0 ${selectedRow ? 'flex-1' : 'w-full'}`}>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search EAID or name…"
              className="w-full text-xs border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-gray-800 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400"
            />
          </div>
          <button
            onClick={() => setShowFilters((s) => !s)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border ${
              showFilters ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-600'
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-amber-500 text-white rounded-full text-[10px] px-1.5">{activeFilterCount}</span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="border border-gray-200 rounded-lg p-3 mb-3 bg-white flex flex-wrap gap-4">
            <ChipGroup label="Disposition" options={DISPS} value={disposition} onChange={setDisposition} />
            <ChipGroup label="Category" options={CATS} value={category} onChange={setCategory} format={(o) => (o === 'any' ? 'Any' : o === 'na' ? 'N/A' : `Cat ${o}`)} />
            <ChipGroup label="Scope" options={SCOPES} value={scope} onChange={setScope} format={(o) => (o === 'any' ? 'Any' : o === 'oos' ? 'Out of scope' : 'In scope')} />
            <ChipGroup label="Assessed" options={ASSESSED} value={assessed} onChange={setAssessed} />
            <ChipGroup
              label="Phase 2 progress"
              options={PROGRESS}
              value={progress}
              onChange={setProgress}
              format={(o) => (o === 'any' ? 'Any' : o.replace(/^\d[\d\s-]*\s*/, '').replace(/^-\s*/, '') || o)}
            />
            {activeFilterCount > 0 && (
              <button
                onClick={() => { setDisposition('any'); setCategory('any'); setScope('any'); setAssessed('any'); setProgress('any'); }}
                className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        )}

        <p className="text-[11px] text-gray-400 mb-2">Showing {items.length} applications</p>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 z-10">
                <tr>
                  {['EAID', 'Name', 'SoR Disp.', 'Tier', 'Progress', 'Category', 'P3 Status', 'Owner', 'Scope'].map((h) => (
                    <th key={h} className="bg-gray-50 text-gray-500 font-semibold text-left px-3 py-2 border-b border-gray-200 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => r.eai_nbr != null && setSelected(r.eai_nbr)}
                    className={`cursor-pointer even:bg-gray-50 hover:bg-amber-50/60 ${selected === r.eai_nbr ? 'bg-amber-50' : ''}`}
                  >
                    <td className="px-3 py-2 font-semibold text-super-dark-purple whitespace-nowrap">{r.eai_nbr ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-800 max-w-[220px] truncate">{r.name}</td>
                    <td className="px-3 py-2">{r.sor_disposition || '—'}</td>
                    <td className="px-3 py-2">{r.sor_tier || '—'}</td>
                    <td className="px-3 py-2 max-w-[160px] truncate">{r.cloud_migration_progress || '—'}</td>
                    <td className="px-3 py-2">{r.cloud_category == null ? 'N/A' : `Cat ${r.cloud_category}`}</td>
                    <td className="px-3 py-2 max-w-[140px] truncate">{r.phase3_status || '—'}</td>
                    <td className="px-3 py-2 max-w-[120px] truncate">{r.phase3_owner || '—'}</td>
                    <td className="px-3 py-2">
                      {r.out_of_scope ? (
                        <span className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">OOS</span>
                      ) : 'In'}
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-400">No applications match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedRow && selected != null && (
        <AppDetailPanel eai={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function ChipGroup<T extends string>({
  label, options, value, onChange, format,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  format?: (o: T) => string;
}) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const active = value === o;
          return (
            <button
              key={o}
              onClick={() => onChange(o)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                active ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-gray-200 bg-white text-gray-500'
              }`}
            >
              {format ? format(o) : o === 'any' ? 'Any' : o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
